import {useState} from 'react';

import {FilterType} from '/enum/filterType.enum';

import {buildDefaultFilters} from '../filters/builders/buildDefaultFilters.util';
import {TabFilters} from '../types/MyTeamsFilters.types';

type TabType = 'my-teams' | 'watchlist';

interface UseMyTeamsFiltersProps {
  ffTeamsOrgSR: boolean;
  optionSelected: (id: string, type: FilterType) => void;
  initialTab?: TabType;
}

export const useMyTeamsFilters = ({
  ffTeamsOrgSR,
  optionSelected,
  initialTab = 'my-teams',
}: UseMyTeamsFiltersProps) => {
  // Helper to build fresh default filters
  const createDefaultFilters = (): TabFilters =>
    buildDefaultFilters(optionSelected, ffTeamsOrgSR, '', false);

  // Initialize filters for both tabs (runs once)
  const [filtersByTab, setFiltersByTab] = useState<Record<TabType, TabFilters>>(
    () => ({
      'my-teams': createDefaultFilters(),
      watchlist: createDefaultFilters(),
    }),
  );

  const [activeTab, setActiveTab] = useState<TabType>(initialTab);

  // Update filters for a specific tab
  const updateTabFilters = (tab: TabType, updates: Partial<TabFilters>) => {
    setFiltersByTab(prev => ({
      ...prev,
      [tab]: {
        ...prev[tab],
        ...updates,
      },
    }));
  };

  // Update filters for the currently active tab
  const updateCurrentTabFilters = (updates: Partial<TabFilters>) => {
    updateTabFilters(activeTab, updates);
  };

  // Reset filters
  const handleResetFilters = (
    tab?: TabType,
    frontlineMyTeams?: boolean,
    frontlineWatchlist?: boolean,
  ) => {
    const defaults = {
      'my-teams': {
        ...createDefaultFilters(),
        frontlineOnly: frontlineMyTeams ?? false,
      },
      watchlist: {
        ...createDefaultFilters(),
        frontlineOnly: frontlineWatchlist ?? false,
      },
    };

    if (tab) {
      setFiltersByTab(prev => ({
        ...prev,
        [tab]: defaults[tab],
      }));
    } else {
      setFiltersByTab(defaults);
    }
  };

  const currentFilters = filtersByTab[activeTab];

  return {
    filtersByTab,
    currentFilters,
    updateTabFilters,
    updateCurrentTabFilters,
    handleResetFilters,
    activeTab,
    setActiveTab,
  };
};
